import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Upload, X, ExternalLink } from "lucide-react";
import {
  fetchTestimonials, createTestimonial, updateTestimonial, deleteTestimonial,
  uploadMedia, mediaUrl, me, clearToken, getToken,
} from "@/lib/api";
import Spinner from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const empty = { company: "", industry: "", quote: "", link: "", image_path: null };

export default function AdminDashboard() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    me().catch(() => { clearToken(); nav("/admin/login"); });
    reload();
  }, [nav]);

  const reload = () => {
    setLoading(true);
    fetchTestimonials()
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  };

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (t) => { setForm({ company: t.company, industry: t.industry, quote: t.quote, link: t.link || "", image_path: t.image_path || null }); setEditing(t.id); };
  const cancel = () => { setEditing(null); setForm(empty); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadMedia(file);
      setForm((f) => ({ ...f, image_path: r.data.path }));
      toast.success("Kuva ladattu");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kuvan lataus epäonnistui");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.company.trim() || !form.industry.trim() || !form.quote.trim()) {
      toast.error("Täytä pakolliset kentät (yritys, toimiala, sitaatti)");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await createTestimonial(form);
        toast.success("Referenssi lisätty");
      } else {
        await updateTestimonial(editing, form);
        toast.success("Referenssi päivitetty");
      }
      cancel();
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Poistetaanko referenssi?")) return;
    try {
      await deleteTestimonial(id);
      toast.success("Referenssi poistettu");
      reload();
    } catch {
      toast.error("Poisto epäonnistui");
    }
  };

  const logout = () => { clearToken(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#0B0D10] text-white" data-testid="admin-dashboard-page">
      <header className="border-b border-white/10 backdrop-blur-xl bg-[#0B0D10]/80 sticky top-0 z-40">
        <div className="md-container flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2" data-testid="admin-home-link">
              <span className="inline-flex items-center justify-center rounded-lg bg-white px-2.5 py-1">
                <img src="/myyntidrive-logo.png?v=7" srcSet="/myyntidrive-logo.png?v=7 1x, /myyntidrive-logo@2x.png?v=7 2x" alt="MyyntiDrive" className="h-6 w-auto block" />
              </span>
              <span className="text-white/50 font-normal text-sm">/ Admin</span>
            </Link>
          </div>
          <button onClick={logout} className="md-btn-secondary text-sm" data-testid="admin-logout">
            <LogOut size={14} /> Kirjaudu ulos
          </button>
        </div>
      </header>

      <main className="md-container py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold">Referenssit</h1>
            <p className="text-white/60 mt-1">Hallitse asiakascaseja etusivun referenssit-osiossa.</p>
          </div>
          <button onClick={startNew} className="md-btn-primary" data-testid="admin-new-testimonial">
            <Plus size={16} /> Lisää uusi
          </button>
        </div>

        {editing && (
          <div className="md-card p-6 md:p-8 mb-8" data-testid="admin-testimonial-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold">{editing === "new" ? "Uusi referenssi" : "Muokkaa referenssiä"}</h2>
              <button onClick={cancel} className="text-white/60 hover:text-white"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-white/80 mb-2 block">Yritys *</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-[#0B0D10] border-white/10 text-white h-11" data-testid="admin-field-company" />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Toimiala *</Label>
                <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="bg-[#0B0D10] border-white/10 text-white h-11" data-testid="admin-field-industry" />
              </div>
            </div>
            <div className="mt-5">
              <Label className="text-white/80 mb-2 block">Sitaatti / tulos *</Label>
              <Textarea rows={3} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="bg-[#0B0D10] border-white/10 text-white" data-testid="admin-field-quote" />
            </div>
            <div className="mt-5">
              <Label className="text-white/80 mb-2 block">Linkki sivuun</Label>
              <Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="bg-[#0B0D10] border-white/10 text-white h-11" placeholder="https://" data-testid="admin-field-link" />
            </div>
            <div className="mt-5">
              <Label className="text-white/80 mb-2 block">Kuva</Label>
              <div className="flex items-center gap-4">
                {form.image_path && (
                  <img src={mediaUrl(form.image_path)} alt="preview" className="h-20 w-32 object-cover rounded-lg border border-white/10" />
                )}
                <label className="md-btn-secondary cursor-pointer" data-testid="admin-upload-label">
                  {uploading ? <Spinner size="sm" label="Ladataan..." /> : <><Upload size={16} /> {form.image_path ? "Vaihda kuva" : "Lataa kuva"}</>}
                  <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} data-testid="admin-upload-input" />
                </label>
                {form.image_path && (
                  <button onClick={() => setForm({ ...form, image_path: null })} className="text-sm text-white/60 hover:text-red-400" data-testid="admin-remove-image">Poista kuva</button>
                )}
              </div>
            </div>
            <div className="mt-8 flex gap-3">
              <button onClick={save} disabled={saving} className="md-btn-primary disabled:opacity-70" data-testid="admin-save">
                {saving ? <Spinner size="sm" label="Tallennetaan..." /> : "Tallenna"}
              </button>
              <button onClick={cancel} className="md-btn-secondary" data-testid="admin-cancel">Peruuta</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="md-card p-16 grid place-items-center" data-testid="admin-loading">
            <Spinner size="lg" label="Ladataan referenssejä..." />
          </div>
        ) : items.length === 0 ? (
          <div className="md-card p-10 text-center text-white/60" data-testid="admin-empty">
            Ei referenssejä vielä. Lisää ensimmäinen yllä olevalla painikkeella.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4" data-testid="admin-testimonials-list">
            {items.map((t) => (
              <div key={t.id} className="md-card p-5 flex items-center gap-5" data-testid={`admin-row-${t.id}`}>
                {t.image_path ? (
                  <img src={mediaUrl(t.image_path)} alt={t.company} className="h-16 w-24 object-cover rounded-lg border border-white/10 shrink-0" />
                ) : (
                  <div className="h-16 w-24 rounded-lg bg-[#0B1A3A] border border-white/10 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="font-display font-semibold text-white">{t.company}</div>
                    <span className="text-xs font-mono uppercase tracking-widest text-[#00E5FF]">{t.industry}</span>
                    {t.link && <a href={t.link} target="_blank" rel="noreferrer" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1"><ExternalLink size={12} /> Sivu</a>}
                  </div>
                  <p className="mt-1 text-sm text-white/70 line-clamp-2">&ldquo;{t.quote}&rdquo;</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => startEdit(t)} className="p-2 rounded-lg border border-white/10 hover:bg-white/5" data-testid={`admin-edit-${t.id}`}><Pencil size={16} /></button>
                  <button onClick={() => remove(t.id)} className="p-2 rounded-lg border border-white/10 hover:bg-red-500/10 hover:text-red-400" data-testid={`admin-delete-${t.id}`}><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
